import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import {
  useReport,
  useUpdateReport,
  useEditWindow,
  EditWindowBadge,
  useReportImages,
  useCreateImageUploadURL,
  useDeleteImage,
} from '@/hooks'
import { PageHeader, ChecklistEditor, NotFound, Forbidden, ImageUpload, ImageGallery } from '@/components'
import type { UploadedImage } from '@/components'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import type { ChecklistItem } from '@/types'

const reportEditSchema = z.object({
  notes: z.string().max(2000, 'Notes are too long').optional(),
})

type ReportEditValues = z.infer<typeof reportEditSchema>

export function ReportEditPage() {
  const { reportId } = useParams<{ reportId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const { data: report, isLoading, error } = useReport(reportId ?? '')
  const updateMutation = useUpdateReport(reportId ?? '')
  const editWindowStatus = useEditWindow(report)
  const { data: existingImages = [] } = useReportImages(reportId ?? '')
  const createUploadMutation = useCreateImageUploadURL(reportId ?? '')
  const deleteImageMutation = useDeleteImage(reportId ?? '')

  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([])
  const [newImages, setNewImages] = useState<UploadedImage[]>([])

  const form = useForm<ReportEditValues>({
    resolver: zodResolver(reportEditSchema),
    defaultValues: {
      notes: '',
    },
  })

  // Populate form when report loads
  useEffect(() => {
    if (report) {
      form.reset({
        notes: report.notes ?? '',
      })
      setChecklistItems(report.checklist)
    }
  }, [report, form])

  // Count existing confirmed images
  const existingImageCount = existingImages.filter(
    (img) => img.status === 'confirmed'
  ).length

  // Handle image deletion
  const handleDeleteImage = useCallback(
    (imageId: string) => {
      deleteImageMutation.mutate(imageId)
    },
    [deleteImageMutation]
  )

  // Get upload URL for new images
  const getUploadUrl = useCallback(
    async (contentType: string, filename: string, contentLength: number) => {
      const response = await createUploadMutation.mutateAsync({
        content_type: contentType,
        filename,
        content_length: contentLength,
      })
      return {
        imageId: response.image_id,
        uploadUrl: response.upload_url,
      }
    },
    [createUploadMutation]
  )

  function onSubmit(values: ReportEditValues) {
    updateMutation.mutate(
      {
        checklist: checklistItems,
        notes: values.notes || undefined,
      },
      {
        onSuccess: () => {
          navigate(`/reports/${reportId}`)
        },
      }
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !report) {
    return <NotFound onBack={() => navigate('/reports')} />
  }

  if (!editWindowStatus.canEdit) {
    return <Forbidden onBack={() => navigate(`/reports/${reportId}`)} />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/reports/${reportId}`)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <PageHeader
          title="Edit Report"
          description={`Editing report from ${new Date(report.created_at).toLocaleDateString()}`}
          actions={<EditWindowBadge status={editWindowStatus} />}
        />
      </div>

      {user?.role === 'employee' && editWindowStatus.isCountingDown && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Edit window active</AlertTitle>
          <AlertDescription>
            You can edit this report for {editWindowStatus.timeRemainingFormatted}.
            After this time, only administrators can make changes.
          </AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Checklist</CardTitle>
            </CardHeader>
            <CardContent>
              <ChecklistEditor
                items={checklistItems}
                onChange={setChecklistItems}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Photos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {existingImageCount > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Existing Photos ({existingImageCount})
                  </h4>
                  <ImageGallery
                    images={existingImages}
                    canDelete={editWindowStatus.canEdit}
                    onDelete={handleDeleteImage}
                    isDeleting={deleteImageMutation.isPending}
                  />
                </div>
              )}
              {existingImageCount < 10 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Add New Photos
                  </h4>
                  <ImageUpload
                    reportId={reportId}
                    images={newImages}
                    onChange={setNewImages}
                    getUploadUrl={getUploadUrl}
                    maxImages={10 - existingImageCount}
                    disabled={!editWindowStatus.canEdit || updateMutation.isPending}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        placeholder="Add any additional notes about this cleaning report (optional)"
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/reports/${reportId}`)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
